import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from "antd";
import type { MenuProps } from "antd";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  MoreOutlined,
  ScheduleOutlined,
  SendOutlined,
  SettingOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  CampaignRegisteredChannelLimit,
  CampaignSummaryItem,
} from "../models/campaign";
import { campaignService } from "../services/campaignService";
import AdminAccessGate from "../components/common/AdminAccessGate";

const { Text } = Typography;

const statusTag = (status: CampaignRegisteredChannelLimit["Status"]) => {
  const isActive = status === "Active";
  return (
    <span
      style={{
        display: "inline-block",
        backgroundColor: isActive ? "#D1FADF" : "#FEE2E2",
        color: isActive ? "#027A48" : "#B91C1C",
        borderRadius: 4,
        padding: "0px 8px",
        fontSize: 11,
        fontWeight: 500,
        lineHeight: "18px",
      }}>
      {status}
    </span>
  );
};

const CardHeaderIcon: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
  <div
    style={{
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: "#E7EFF9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
    {icon}
  </div>
);

type SendLimitFormValues = {
  minSendLimit: number;
  maxSendLimit: number;
};

type RescheduleFormValues = {
  campaignDate: Dayjs;
};

const RegisteredChannelSendLimitContent: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState<CampaignRegisteredChannelLimit[]>(
    [],
  );
  const [editTarget, setEditTarget] =
    useState<CampaignRegisteredChannelLimit | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<SendLimitFormValues>();
  const [deactivateTarget, setDeactivateTarget] =
    useState<CampaignRegisteredChannelLimit | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] =
    useState<CampaignRegisteredChannelLimit | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleForm] = Form.useForm<RescheduleFormValues>();
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [summaryDate, setSummaryDate] = useState<Dayjs>(dayjs());
  const [summary, setSummary] = useState<CampaignSummaryItem[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryPage, setSummaryPage] = useState(1);

  const fetchChannels = async () => {
    setLoading(true);
    const result = await campaignService.getAllCampaignRegisteredChannel();
    setChannels(result ?? []);
    setLoading(false);
  };

  const fetchSummary = async (date: Dayjs) => {
    setSummaryLoading(true);
    const result = await campaignService.getCampaignSummary(
      date.format("YYYY-MM-DD"),
    );
    setSummary(result ?? []);
    setSummaryLoading(false);
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  useEffect(() => {
    fetchSummary(summaryDate);
    setSummaryPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryDate]);

  const summaryTotals = useMemo(
    () =>
      summary.reduce(
        (acc, item) => ({
          campaigns: acc.campaigns + 1,
          targetMembers: acc.targetMembers + item.TotalTargetMember,
          success: acc.success + item.TotalSuccess,
          failed: acc.failed + item.TotalFailed,
          pending: acc.pending + item.TotalPending,
          sentToday: acc.sentToday + item.SentToday,
        }),
        {
          campaigns: 0,
          targetMembers: 0,
          success: 0,
          failed: 0,
          pending: 0,
          sentToday: 0,
        },
      ),
    [summary],
  );

  const openEditModal = (record: CampaignRegisteredChannelLimit) => {
    setEditTarget(record);
    form.setFieldsValue({
      minSendLimit: record.MinSendLimit ?? 0,
      maxSendLimit: record.MaxSendLimit ?? 0,
    });
  };

  const closeEditModal = () => {
    setEditTarget(null);
    form.resetFields();
  };

  const handleSave = async () => {
    if (!editTarget) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const success =
        await campaignService.updateCampaignRegisteredChannelSendLimit(
          editTarget.ClientId,
          values.minSendLimit,
          values.maxSendLimit,
        );
      if (success) {
        message.success("Send limit updated successfully.");
        closeEditModal();
        await fetchChannels();
      } else {
        message.error("Failed to update send limit. Please try again.");
      }
    } catch (err) {
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    const success = await campaignService.updateCampaignRegisteredChannelStatus(
      deactivateTarget.ClientId,
      0,
    );
    if (success) {
      message.success("Channel deactivated successfully.");
      setDeactivateTarget(null);
      await fetchChannels();
    } else {
      message.error("Failed to deactivate channel. Please try again.");
    }
    setDeactivating(false);
  };

  const openRescheduleModal = (record: CampaignRegisteredChannelLimit) => {
    setRescheduleTarget(record);
    rescheduleForm.resetFields();
  };

  const closeRescheduleModal = () => {
    setRescheduleTarget(null);
    rescheduleForm.resetFields();
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget?.RunningCampaign) return;
    try {
      const values = await rescheduleForm.validateFields();
      setRescheduling(true);
      const success =
        await campaignService.bulkUpdateCampaignMemberLogsSendDateByRunLog(
          rescheduleTarget.RunningCampaign.CampaignRunLogId,
          values.campaignDate.format("YYYY-MM-DD HH:mm"),
        );
      if (success) {
        message.success("Campaign send date updated successfully.");
        closeRescheduleModal();
      } else {
        message.error("Failed to update campaign send date. Please try again.");
      }
    } catch (err) {
    } finally {
      setRescheduling(false);
    }
  };

  const summaryColumns = [
    {
      title: "No",
      key: "No",
      width: 60,
      render: (_: unknown, __: CampaignSummaryItem, index: number) =>
        (summaryPage - 1) * pageSize + index + 1,
    },
    {
      title: "Brand",
      dataIndex: "BrandName",
      key: "BrandName",
      width: 170,
    },
    {
      title: "Campaign",
      key: "Campaign",
      render: (_: unknown, record: CampaignSummaryItem) => {
        const total = record.TotalTargetMember || 1;
        const successPct = (record.TotalSuccess / total) * 100;
        const failedPct = (record.TotalFailed / total) * 100;
        return (
          <div style={{ minWidth: 200 }}>
            <div>{record.CampaignName}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Campaign #{record.CampaignId} &middot; Run #
              {record.CampaignRunLogId}
            </Text>
            <div
              style={{
                display: "flex",
                height: 4,
                borderRadius: 2,
                overflow: "hidden",
                marginTop: 6,
                backgroundColor: "#F1F5F9",
              }}>
              <div
                style={{ width: `${successPct}%`, backgroundColor: "#12B76A" }}
              />
              <div
                style={{ width: `${failedPct}%`, backgroundColor: "#F04438" }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Target",
      dataIndex: "TotalTargetMember",
      key: "TotalTargetMember",
      align: "right" as const,
      width: 90,
    },
    {
      title: "Success",
      dataIndex: "TotalSuccess",
      key: "TotalSuccess",
      align: "right" as const,
      width: 90,
      render: (v: number) => <Text style={{ color: "#027A48" }}>{v}</Text>,
    },
    {
      title: "Failed",
      dataIndex: "TotalFailed",
      key: "TotalFailed",
      align: "right" as const,
      width: 90,
      render: (v: number) => <Text style={{ color: "#B91C1C" }}>{v}</Text>,
    },
    {
      title: "Pending",
      dataIndex: "TotalPending",
      key: "TotalPending",
      align: "right" as const,
      width: 90,
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: "Sent Today",
      key: "SentToday",
      align: "right" as const,
      width: 130,
      render: (_: unknown, record: CampaignSummaryItem) => (
        <div>
          <div>{record.SentToday}</div>
          <Text style={{ fontSize: 12, color: "#027A48" }}>
            {record.SentTodaySuccess}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {" / "}
          </Text>
          <Text style={{ fontSize: 12, color: "#B91C1C" }}>
            {record.SentTodayFailed}
          </Text>
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "No",
      key: "No",
      width: 60,
      render: (_: unknown, __: CampaignRegisteredChannelLimit, index: number) =>
        (page - 1) * pageSize + index + 1,
    },
    {
      title: "Merchant",
      dataIndex: "BrandName",
      key: "BrandName",
      width: 170,
    },
    {
      title: "Client ID",
      dataIndex: "ClientId",
      key: "ClientId",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 100,
      render: (status: CampaignRegisteredChannelLimit["Status"]) =>
        statusTag(status),
    },
    {
      title: "Running Campaign",
      dataIndex: "RunningCampaign",
      key: "RunningCampaign",
      render: (running: CampaignRegisteredChannelLimit["RunningCampaign"]) =>
        running ? (
          <Text type="secondary">
            Campaign #{running.CampaignDetailsId} &middot; Run #
            {running.CampaignRunLogId}
          </Text>
        ) : (
          <Text type="secondary">&mdash;</Text>
        ),
    },
    {
      title: "Min Limit",
      dataIndex: "MinSendLimit",
      key: "MinSendLimit",
      align: "right" as const,
      width: 100,
    },
    {
      title: "Max Limit",
      dataIndex: "MaxSendLimit",
      key: "MaxSendLimit",
      align: "right" as const,
      width: 100,
    },
    {
      title: "",
      key: "action",
      align: "right" as const,
      width: 56,
      render: (_: unknown, record: CampaignRegisteredChannelLimit) => {
        const items: MenuProps["items"] = [
          {
            key: "edit",
            label: "Edit Send Limit",
            icon: <EditOutlined />,
            onClick: () => openEditModal(record),
          },
          ...(record.RunningCampaign
            ? [
                {
                  key: "reschedule",
                  label: "Reschedule Send Date",
                  icon: <ScheduleOutlined />,
                  onClick: () => openRescheduleModal(record),
                },
              ]
            : []),
          ...(record.Status === "Active"
            ? [
                {
                  key: "deactivate",
                  label: "Deactivate",
                  icon: <StopOutlined />,
                  danger: true,
                  onClick: () => setDeactivateTarget(record),
                },
              ]
            : []),
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space align="center" size={12}>
            <CardHeaderIcon
              icon={
                <BarChartOutlined style={{ fontSize: 18, color: "#1565C0" }} />
              }
            />
            <div>
              <span style={{ fontSize: 20 }}>Campaign Summary</span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: "normal",
                  color: "#8c8c8c",
                }}>
                Delivery progress of sent campaigns for the selected date.
              </div>
            </div>
          </Space>
        }
        extra={
          <DatePicker
            value={summaryDate}
            onChange={(value) => value && setSummaryDate(value)}
            allowClear={false}
            format="DD MMM YYYY"
          />
        }>
        {summary.length > 0 && (
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Campaigns"
                value={summaryTotals.campaigns}
                prefix={<TeamOutlined style={{ color: "#8c8c8c" }} />}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Target Members"
                value={summaryTotals.targetMembers}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Success"
                value={summaryTotals.success}
                valueStyle={{ color: "#027A48" }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Failed"
                value={summaryTotals.failed}
                valueStyle={{ color: "#B91C1C" }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Pending" value={summaryTotals.pending} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Sent Today"
                value={summaryTotals.sentToday}
                valueStyle={{ color: "#1565C0" }}
                prefix={<SendOutlined />}
              />
            </Col>
          </Row>
        )}
        {summary.length > 0 || summaryLoading ? (
          <Table
            rowKey="CampaignRunLogId"
            loading={summaryLoading}
            dataSource={summary}
            columns={summaryColumns as any}
            rowClassName={(_, index) =>
              index % 2 === 1 ? "rcsl-row-alt" : ""
            }
            pagination={{
              current: summaryPage,
              pageSize,
              onChange: (p) => setSummaryPage(p),
            }}
            scroll={{ x: "max-content" }}
          />
        ) : (
          <div
            style={{
              minHeight: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Space direction="vertical" align="center">
              <Empty description={false} />
              <Text type="secondary">
                No sent campaigns found for this date.
              </Text>
            </Space>
          </div>
        )}
      </Card>

      <Card
        title={
          <Space align="center" size={12}>
            <CardHeaderIcon
              icon={
                <SettingOutlined style={{ fontSize: 18, color: "#1565C0" }} />
              }
            />
            <div>
              <span style={{ fontSize: 20 }}>
                Channel &amp; Campaign Configuration
              </span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: "normal",
                  color: "#8c8c8c",
                }}>
                Manage send limits and activation per registered channel, and
                reschedule the send date of a channel's running campaign.
              </div>
            </div>
          </Space>
        }
        style={{ marginTop: 16 }}>
        {channels.length > 0 || loading ? (
          <Table
            rowKey="ClientId"
            loading={loading}
            dataSource={channels}
            columns={columns as any}
            rowClassName={(_, index) =>
              index % 2 === 1 ? "rcsl-row-alt" : ""
            }
            pagination={{
              current: page,
              pageSize,
              onChange: (p) => setPage(p),
            }}
            scroll={{ x: "max-content" }}
          />
        ) : (
          <div
            style={{
              minHeight: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Space direction="vertical" align="center">
              <Empty description={false} />
              <Text type="secondary">
                No registered channels or campaign configurations found.
              </Text>
            </Space>
          </div>
        )}
      </Card>

      <Modal
        open={editTarget !== null}
        onCancel={closeEditModal}
        onOk={handleSave}
        confirmLoading={saving}
        title="Edit Send Limit"
        destroyOnClose
        okText="Save Changes">
        {editTarget && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              {editTarget.BrandName} &middot; {editTarget.ClientId}
            </Text>
          </div>
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            label="Min Send Limit"
            name="minSendLimit"
            rules={[
              { required: true, message: "Min send limit is required." },
              {
                type: "number",
                min: 0,
                message: "Min send limit cannot be negative.",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const max = getFieldValue("maxSendLimit");
                  if (
                    value == null ||
                    max == null ||
                    Number(value) <= Number(max)
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Min send limit cannot exceed max send limit."),
                  );
                },
              }),
            ]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item
            label="Max Send Limit"
            name="maxSendLimit"
            dependencies={["minSendLimit"]}
            rules={[
              { required: true, message: "Max send limit is required." },
              {
                type: "number",
                min: 0,
                message: "Max send limit cannot be negative.",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const min = getFieldValue("minSendLimit");
                  if (
                    value == null ||
                    min == null ||
                    Number(value) >= Number(min)
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      "Max send limit cannot be less than min send limit.",
                    ),
                  );
                },
              }),
            ]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={deactivateTarget !== null}
        onCancel={() => setDeactivateTarget(null)}
        onOk={handleDeactivate}
        confirmLoading={deactivating}
        title="Deactivate Channel"
        destroyOnClose
        okText="Deactivate"
        okButtonProps={{ danger: true }}>
        {deactivateTarget && (
          <Text>
            Are you sure you want to deactivate{" "}
            <Text strong>
              {deactivateTarget.BrandName} &middot; {deactivateTarget.ClientId}
            </Text>
            ? This channel will no longer be able to send campaign messages
            until reactivated.
          </Text>
        )}
      </Modal>

      <Modal
        open={rescheduleTarget !== null}
        onCancel={closeRescheduleModal}
        onOk={handleReschedule}
        confirmLoading={rescheduling}
        title="Reschedule Campaign Send Date"
        destroyOnClose
        okText="Update Send Date">
        {rescheduleTarget?.RunningCampaign && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              {rescheduleTarget.BrandName} &middot; {rescheduleTarget.ClientId}{" "}
              &middot; Campaign #
              {rescheduleTarget.RunningCampaign.CampaignDetailsId} (Run #
              {rescheduleTarget.RunningCampaign.CampaignRunLogId})
            </Text>
          </div>
        )}
        <Form form={rescheduleForm} layout="vertical">
          <Form.Item
            label="New Send Date"
            name="campaignDate"
            rules={[{ required: true, message: "Send date is required." }]}>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const RegisteredChannelSendLimit: React.FC = () => (
  <AdminAccessGate>
    <RegisteredChannelSendLimitContent />
  </AdminAccessGate>
);

export default RegisteredChannelSendLimit;
